import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Avatar,
  Button,
  IconButton,
  CircularProgress,
  Paper,
  Container,
  LinearProgress,
  Divider,
  Chip,
  InputAdornment,
} from "@mui/material";
import {
  Camera,
  Trash2,
  User,
  Building,
  Phone,
  Mail,
  Save,
  Link as LinkIcon,
  Briefcase,
  FileText,
  ChevronRight,
  CheckCircle2,
  XCircle,
  MapPin,
  Globe,
  Shield,
} from "lucide-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { motion, AnimatePresence } from "framer-motion";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import SideNavbar from "../../../components/sideNavbar";
import Header from "../../../components/header";
import CustomSnackbar from "../../../components/CustomSnackbar";
import axiosInstance from "../../../services/axiosInstance";

const baseURL = process.env.REACT_APP_JUSTTAWK_IND_BASE_API_URL;

function Profile() {
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const fileInputRef = useRef(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const userId = localStorage.getItem("userId");
  const token = localStorage.getItem("token");

  const handleCloseSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  useEffect(() => { getData(); }, []);

  const getData = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`${apiEndPointsConfig.getUserProfile}/${userId}`, {
        headers: { Authorization: token },
      });
      setUserDetails(res?.data?.user);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (data) => {
    try {
      await axiosInstance.put(apiEndPointsConfig.updateUserProfile, { ...data, userId }, {
        headers: { Authorization: token },
      });
      setSnackbar({ open: true, message: "Profile updated successfully!", severity: "success" });
      getData();
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to update profile.", severity: "error" });
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("photo", file);
    formData.append("userId", userId);
    setUploading(true);
    try {
      await axiosInstance.post(apiEndPointsConfig.uploadProfilePhoto, formData, {
        headers: { Authorization: token, "Content-Type": "multipart/form-data" },
      });
      setSnackbar({ open: true, message: "Photo updated!", severity: "success" });
      getData();
    } catch (error) {
      setSnackbar({ open: true, message: "Upload failed.", severity: "error" });
    } finally {
      setUploading(false);
    }
  };

  const deletePhoto = async () => {
    try {
      await axiosInstance.delete(`${apiEndPointsConfig.deleteProfilePhoto}/${userId}`, {
        headers: { Authorization: token },
      });
      setSnackbar({ open: true, message: "Photo removed.", severity: "success" });
      getData();
    } catch (error) {
      setSnackbar({ open: true, message: "Delete failed.", severity: "error" });
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: userDetails?.name || "",
      email: userDetails?.email || "",
      phoneNumber: userDetails?.phoneNumber || "",
      companyName: userDetails?.companyName || "",
      industryType: userDetails?.industryType || "",
      customerCareNo: userDetails?.customerCareNo || "",
      website: userDetails?.website || "",
      address: userDetails?.address || "",
      bio: userDetails?.bio || "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Required"),
      email: Yup.string().email("Invalid email").required("Required"),
    }),
    onSubmit: (values) => updateProfile(values),
  });

  if (loading || !userDetails) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', ml: { md: '280px' }, gap: 2 }}>
        <CircularProgress thickness={3} size={48} sx={{ color: '#6366f1' }} />
        <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 600 }}>Loading your profile…</Typography>
      </Box>
    );
  }

  const completionItems = [
    { label: "Full name", done: !!userDetails?.name },
    { label: "Email address", done: !!userDetails?.email },
    { label: "Phone number", done: !!userDetails?.phoneNumber },
    { label: "Company info", done: !!userDetails?.companyName },
    { label: "Profile photo", done: !!userDetails?.profilePhoto },
    { label: "Biography", done: !!userDetails?.bio },
  ];
  const completion = Math.round((completionItems.filter(c => c.done).length / completionItems.length) * 100);

  const sections = [
    { id: 0, label: "Identity", icon: User, desc: "Personal information" },
    { id: 1, label: "Business", icon: Briefcase, desc: "Company details" },
    { id: 2, label: "Narrative", icon: FileText, desc: "Bio & summary" },
  ];

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 3,
      bgcolor: '#f8fafc',
      transition: 'all 0.2s',
      '&:hover': { bgcolor: '#f1f5f9' },
      '&.Mui-focused': { bgcolor: '#fff', boxShadow: '0 0 0 3px rgba(99,102,241,0.12)' },
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
    '& .MuiInputLabel-root': { fontWeight: 600, color: '#94a3b8' },
  };

  return (
    <Box sx={{ ml: { xs: "0px", md: "280px" }, bgcolor: "#f1f5f9", minHeight: "100vh" }}>
      <SideNavbar />
      {/* <Header title="Account Settings" /> */}

      {/* Cover Banner */}
      <Box
        sx={{
          height: 100,
          background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #db2777 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative blobs */}
        <Box sx={{ position: 'absolute', top: -60, right: -60, width: 280, height: 280, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.07)' }} />
        <Box sx={{ position: 'absolute', bottom: -40, left: 120, width: 180, height: 180, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)' }} />
        <Box sx={{ position: 'absolute', top: 20, left: '40%', width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
      </Box>

      <Container maxWidth="lg" sx={{ mt: -6, pb: 10, position: 'relative', zIndex: 2 }}>
        <Grid container spacing={3}>

          {/* LEFT SIDEBAR */}
          <Grid item xs={12} md={3.5}>

            {/* Profile Card */}
            <Paper
              elevation={0}
              sx={{ borderRadius: 5, border: '1px solid #e2e8f0', overflow: 'hidden', mb: 3, bgcolor: '#fff' }}
            >
              {/* Avatar section with gradient strip */}
              <Box sx={{ height: 70, background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' }} />
              <Box sx={{ px: 3, pb: 3, textAlign: 'center', mt: -7 }}>
                <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
                  <motion.div whileHover={{ scale: 1.04 }} transition={{ type: "spring", stiffness: 300 }}>
                    <Avatar
                      src={userDetails?.profilePhoto ? `${baseURL}${userDetails.profilePhoto}` : ""}
                      sx={{
                        width: 100, height: 100,
                        border: '4px solid #fff',
                        boxShadow: '0 8px 24px rgba(99,102,241,0.25)',
                        bgcolor: '#6366f1',
                        fontSize: '2.5rem',
                        fontWeight: 900,
                      }}
                    >
                      {!userDetails?.profilePhoto && (userDetails?.name?.charAt(0)?.toUpperCase() || <User size={44} />)}
                    </Avatar>
                  </motion.div>

                  {uploading ? (
                    <Box sx={{ position: 'absolute', bottom: 2, right: 2, bgcolor: '#fff', borderRadius: '50%', p: 0.5 }}>
                      <CircularProgress size={20} sx={{ color: '#6366f1' }} />
                    </Box>
                  ) : (
                    <IconButton
                      onClick={() => fileInputRef.current.click()}
                      size="small"
                      sx={{
                        position: 'absolute', bottom: 2, right: 2,
                        bgcolor: '#6366f1', color: '#fff',
                        width: 28, height: 28,
                        boxShadow: '0 2px 8px rgba(99,102,241,0.4)',
                        '&:hover': { bgcolor: '#4f46e5' },
                        border: '2px solid #fff',
                      }}
                    >
                      <Camera size={14} />
                    </IconButton>
                  )}
                  <input type="file" hidden ref={fileInputRef} onChange={handlePhotoUpload} accept="image/*" />
                </Box>

                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', mb: 0.5 }}>
                  {userDetails.name}
                </Typography>
                <Chip
                  label={userDetails.industryType || "Professional"}
                  size="small"
                  sx={{ bgcolor: '#eef2ff', color: '#6366f1', fontWeight: 700, fontSize: '0.72rem', mb: 2 }}
                />

                <Divider sx={{ my: 2 }} />

                <Box sx={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 0.8, borderRadius: 2, bgcolor: '#eff6ff', display: 'flex' }}>
                      <Mail size={14} color="#3b82f6" />
                    </Box>
                    <Typography variant="caption" sx={{ color: '#475569', fontWeight: 600, wordBreak: 'break-all' }}>
                      {userDetails.email}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 0.8, borderRadius: 2, bgcolor: '#f0fdf4', display: 'flex' }}>
                      <Phone size={14} color="#22c55e" />
                    </Box>
                    <Typography variant="caption" sx={{ color: '#475569', fontWeight: 600 }}>
                      {userDetails.phoneNumber || "Not provided"}
                    </Typography>
                  </Box>
                  {userDetails.companyName && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ p: 0.8, borderRadius: 2, bgcolor: '#fdf4ff', display: 'flex' }}>
                        <Building size={14} color="#a855f7" />
                      </Box>
                      <Typography variant="caption" sx={{ color: '#475569', fontWeight: 600 }}>
                        {userDetails.companyName}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {userDetails?.profilePhoto && (
                  <Button
                    fullWidth
                    startIcon={<Trash2 size={14} />}
                    onClick={deletePhoto}
                    size="small"
                    sx={{ mt: 2.5, color: '#ef4444', textTransform: 'none', fontWeight: 700, borderRadius: 2, '&:hover': { bgcolor: '#fef2f2' } }}
                  >
                    Remove Photo
                  </Button>
                )}
              </Box>
            </Paper>


            {/* Account Status */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 5, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#f0fdf4', display: 'flex' }}>
                  <Shield size={16} color="#22c55e" />
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 800, color: '#1e293b' }}>Account Verified</Typography>
              </Box>
              <Typography variant="caption" sx={{ color: '#64748b', lineHeight: 1.6, display: 'block' }}>
                Your account is active and in good standing.
              </Typography>
            </Paper>
          </Grid>

          {/* MAIN CONTENT */}
          <Grid item xs={12} md={8.5}>
            <Paper elevation={0} sx={{ borderRadius: 5, border: '1px solid #e2e8f0', overflow: 'hidden', bgcolor: '#fff' }}>

              {/* Section navigation pills */}
              <Box sx={{ px: 4, pt: 4, pb: 0, borderBottom: '1px solid #f1f5f9' }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {sections.map((sec) => {
                    const Icon = sec.icon;
                    const isActive = activeTab === sec.id;
                    return (
                      <Box
                        key={sec.id}
                        onClick={() => setActiveTab(sec.id)}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1,
                          px: 2.5, py: 1.2,
                          borderRadius: '12px 12px 0 0',
                          cursor: 'pointer',
                          bgcolor: isActive ? '#6366f1' : 'transparent',
                          color: isActive ? '#fff' : '#64748b',
                          fontWeight: 700,
                          fontSize: '0.875rem',
                          transition: 'all 0.18s ease',
                          borderBottom: isActive ? '2px solid #6366f1' : '2px solid transparent',
                          '&:hover': {
                            bgcolor: isActive ? '#6366f1' : '#f8fafc',
                            color: isActive ? '#fff' : '#6366f1',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex' }}><Icon size={15} /></Box>
                        {sec.label}
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              <Box sx={{ p: { xs: 3, md: 5 } }}>
                <form onSubmit={formik.handleSubmit}>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeTab}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -12 }}
                      transition={{ duration: 0.18 }}
                    >
                      {/* ── IDENTITY TAB ── */}
                      {activeTab === 0 && (
                        <Box>
                          <Box sx={{ mb: 4 }}>
                            <Typography variant="h5" sx={{ fontWeight: 900, color: '#1e293b', mb: 0.5, letterSpacing: '-0.01em' }}>
                              Identity & Contact
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                              Keep your personal information accurate and up to date.
                            </Typography>
                          </Box>
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', mb: 0.8, display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.68rem' }}>Full Name</Typography>
                              <TextField
                                fullWidth
                                {...formik.getFieldProps("name")}
                                error={formik.touched.name && !!formik.errors.name}
                                helperText={formik.touched.name && formik.errors.name}
                                placeholder="e.g. John Doe"
                                sx={fieldSx}
                                InputProps={{ startAdornment: <InputAdornment position="start"><Box sx={{ display: 'flex', color: '#94a3b8' }}><User size={16} /></Box></InputAdornment> }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', mb: 0.8, display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.68rem' }}>Email Address</Typography>
                              <TextField
                                fullWidth
                                {...formik.getFieldProps("email")}
                                error={formik.touched.email && !!formik.errors.email}
                                helperText={formik.touched.email && formik.errors.email}
                                placeholder="john@example.com"
                                sx={fieldSx}
                                InputProps={{ startAdornment: <InputAdornment position="start"><Box sx={{ display: 'flex', color: '#94a3b8' }}><Mail size={16} /></Box></InputAdornment> }}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', mb: 0.8, display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.68rem' }}>Phone Number</Typography>
                              <TextField
                                fullWidth
                                {...formik.getFieldProps("phoneNumber")}
                                placeholder="+971 50 000 0000"
                                sx={fieldSx}
                                InputProps={{ startAdornment: <InputAdornment position="start"><Box sx={{ display: 'flex', color: '#94a3b8' }}><Phone size={16} /></Box></InputAdornment> }}
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      )}

                      {/* ── BUSINESS TAB ── */}
                      {activeTab === 1 && (
                        <Box>
                          <Box sx={{ mb: 4 }}>
                            <Typography variant="h5" sx={{ fontWeight: 900, color: '#1e293b', mb: 0.5, letterSpacing: '-0.01em' }}>
                              Business Workspace
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                              Configure your organization details for a professional presence.
                            </Typography>
                          </Box>
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', mb: 0.8, display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.68rem' }}>Company Name</Typography>
                              <TextField fullWidth {...formik.getFieldProps("companyName")} placeholder="Acme Corp" sx={fieldSx}
                                InputProps={{ startAdornment: <InputAdornment position="start"><Box sx={{ display: 'flex', color: '#94a3b8' }}><Building size={16} /></Box></InputAdornment> }} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', mb: 0.8, display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.68rem' }}>Industry Sector</Typography>
                              <TextField fullWidth {...formik.getFieldProps("industryType")} placeholder="e.g. Technology" sx={fieldSx}
                                InputProps={{ startAdornment: <InputAdornment position="start"><Box sx={{ display: 'flex', color: '#94a3b8' }}><Briefcase size={16} /></Box></InputAdornment> }} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', mb: 0.8, display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.68rem' }}>Official Website</Typography>
                              <TextField fullWidth {...formik.getFieldProps("website")} placeholder="https://example.com" sx={fieldSx}
                                InputProps={{ startAdornment: <InputAdornment position="start"><Box sx={{ display: 'flex', color: '#94a3b8' }}><Globe size={16} /></Box></InputAdornment> }} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', mb: 0.8, display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.68rem' }}>Support Hotline</Typography>
                              <TextField fullWidth {...formik.getFieldProps("customerCareNo")} placeholder="+971 4 000 0000" sx={fieldSx}
                                InputProps={{ startAdornment: <InputAdornment position="start"><Box sx={{ display: 'flex', color: '#94a3b8' }}><Phone size={16} /></Box></InputAdornment> }} />
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', mb: 0.8, display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.68rem' }}>Office Address</Typography>
                              <TextField fullWidth multiline rows={3} {...formik.getFieldProps("address")} placeholder="123 Business Bay, Dubai, UAE" sx={fieldSx}
                                InputProps={{ startAdornment: <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}><Box sx={{ display: 'flex', color: '#94a3b8' }}><MapPin size={16} /></Box></InputAdornment> }} />
                            </Grid>
                          </Grid>
                        </Box>
                      )}

                      {/* ── NARRATIVE TAB ── */}
                      {activeTab === 2 && (
                        <Box>
                          <Box sx={{ mb: 4 }}>
                            <Typography variant="h5" sx={{ fontWeight: 900, color: '#1e293b', mb: 0.5, letterSpacing: '-0.01em' }}>
                              Your Narrative
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                              Write a compelling professional story that showcases your expertise.
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              p: 3, borderRadius: 4,
                              background: 'linear-gradient(135deg, #fafafa 0%, #f8fafc 100%)',
                              border: '1px solid #e2e8f0',
                            }}
                          >
                            <TextField
                              fullWidth multiline rows={12}
                              {...formik.getFieldProps("bio")}
                              placeholder="Hi, I'm [your name], a passionate professional with experience in..."
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  bgcolor: 'transparent',
                                  '& fieldset': { border: 'none' },
                                  fontSize: '0.97rem',
                                  lineHeight: 1.75,
                                  p: 0.5,
                                  color: '#334155',
                                },
                              }}
                            />
                          </Box>
                          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                            <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>
                              {(formik.values.bio || "").length} characters
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </motion.div>
                  </AnimatePresence>

                  {/* Form Footer */}
                  <Box
                    sx={{
                      mt: 6, pt: 4,
                      borderTop: '1px solid #f1f5f9',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 2,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>
                      Changes save to your profile immediately
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => getData()}
                        sx={{
                          borderRadius: 2.5, px: 4, textTransform: 'none', fontWeight: 700,
                          borderColor: '#e2e8f0', color: '#64748b',
                          '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' }
                        }}
                      >
                        Discard
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        startIcon={<Save size={18} />}
                        sx={{
                          borderRadius: 2.5, px: 5, py: 1.3,
                          textTransform: 'none',
                          fontWeight: 800,
                          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                          boxShadow: '0 8px 20px rgba(99,102,241,0.3)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                            boxShadow: '0 12px 28px rgba(99,102,241,0.4)',
                            transform: 'translateY(-1px)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        Save Changes
                      </Button>
                    </Box>
                  </Box>
                </form>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <CustomSnackbar open={snackbar.open} onClose={handleCloseSnackbar} message={snackbar.message} severity={snackbar.severity} vertical="bottom" horizontal="center" />
    </Box>
  );
}

export default Profile;